import { compareDesc, parseISO } from 'date-fns';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';

import { Api, useApiFormSubmit } from '../../api';
import { Spacer, Text } from '../../components';
import { FieldSelect, FieldText, FormWrap, SubmitButton } from '../../form';
import { useMyUserClient } from '../../hooks';
import { Model } from '../../models';
import BuildEventDetail from './BuildEventDetail';

type Props = Readonly<{
  build: {
    ['@id']: string;
    ['@type']: 'Build';
    id: string;
    buildNumber: number;
    environment: string;
    platform: string | null;
    link: string;
    qaApprovedAt: string | null;
    qaApprovedBy: Api.User.BaseView | null;
    expired: boolean;
    createdAt: string;
    createdBy: Api.User.BaseView;
    buildEvents: Array<{
      ['@id']: string;
      ['@type']: 'BuildEvent';
      id: string;
      type: string;
      percent: number | null;
      recordedAt: string;
    }>;
  };
  invalidate: () => unknown;
}>;

export default function BuildEvents({ build, invalidate }: Props) {
  const myUser = useMyUserClient();

  const [adding, setAdding] = React.useState(false);

  type FormValues = Omit<Api.BuildEvent.W, 'percent'> & {
    percent: string | null;
  };

  const formSubmit = useApiFormSubmit('POST', '/build-events', { invalidate });
  const form = useForm<Partial<FormValues>>({
    defaultValues: {
      build: `/builds/${build.id}`,
      type: '',
      percent: null,
    },
    onSubmit: (values, helpers) =>
      formSubmit(
        {
          ...values,
          percent: values.percent ? parseInt(values.percent, 10) : null,
        },
        helpers
      ),
  });

  if (!build.qaApprovedAt) return null;

  const buildEvents = build.buildEvents.sort((a, b) =>
    compareDesc(parseISO(a.recordedAt), parseISO(b.recordedAt))
  );

  return (
    <>
      <html.hr style={styles.divider} />
      <html.div style={styles.container}>
        {myUser.agencyRole && (
          <html.a
            style={styles.promptButton}
            onClick={() => setAdding(!adding)}
          >
            <i className="fa-solid fa-plus" />
          </html.a>
        )}
        {adding && (
          <FormWrap
            formErrorList={form.formErrorList}
            handleSubmit={form.handleSubmit}
          >
            <FieldSelect
              choices={Model.BuildEvent.TYPE_CHOICES}
              field={form.getField('type')}
            />
            <FieldText field={form.getField('percent')} />
            <SubmitButton {...form} label="Submit" />
            <Spacer />
          </FormWrap>
        )}
        {buildEvents.length === 0 ? (
          <Text as="p" style={styles.noEvents}>
            No events yet
          </Text>
        ) : (
          buildEvents.map((buildEvent, i) => (
            <React.Fragment key={buildEvent['@id']}>
              {i > 0 && <Spacer size={0.25} />}
              <BuildEventDetail
                buildEvent={buildEvent}
                isFirst={i === 0}
                invalidate={invalidate}
              />
            </React.Fragment>
          ))
        )}
      </html.div>
    </>
  );
}

const styles = css.create({
  container: {
    position: 'relative',
  },
  promptButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: '#0366d6',
    cursor: 'pointer',
    fontSize: 16,
    transition: 'color 0.2s',
    marginLeft: 10,
    ':hover': {
      color: '#0366d6',
    },
  },
  noEvents: {
    color: '#7f858b',
    margin: 0,
  },
  divider: {
    height: 1,
    borderWidth: 0,
    backgroundColor: '#eee',
    marginBlock: 20,
  },
});
